var CacheStore;

/**
 * A simple module that shows data as key-value pairs and automatically deletes
 * them once their expiration time has been reached.
 */
module.exports = CacheStore = function() {
    this.data   = {};
    this.timers = {};
};

// API
// ----------------

/**
 * Stores a key-value pair in the cache. It will be automatically deleted when
 * lifetime expires.
 *
 * @param  {string}  key       The key
 * @param  {string}  value     The value
 * @param  {number}  lifetime  The lifetime of the value in seconds
 * @public
 */
CacheStore.prototype.set = function(key, value, lifetime) {
    this._clearExpiration(key);
    this._setData(key, value);
    this._setExpiration(key, lifetime);
};

/**
 * Clears a key-value pair from the cache.
 *
 * @param  {string}  key  The key
 * @public
 */
CacheStore.prototype.unset = function(key) {
    this._clearExpiration(key);
    this._clearData(key);
};

/**
 * Retrieves the value stored at the provided key.
 *
 * @param    {string}  key  The key
 * @returns  {*}       The value
 * @public
 */
CacheStore.prototype.get = function(key) {
    return this._getData(key);
};

/**
 * Retrieves the value stored at the provided key.
 *
 * @param    {string}   key  The key
 * @returns  {boolean}  `true` if a value is set, `false` otherwise
 * @public
 */
CacheStore.prototype.isset = function(key) {
    return this.data.hasOwnProperty(key);
};

/**
 * Resets the cache completely.
 *
 * @public
 */
CacheStore.prototype.reset = function() {
    var key;

    /* eslint-disable guard-for-in */
    for (key in this.timers) {
        this._clearExpiration(key);
    }

    for (key in this.data) {
        this._clearData(key);
    }
    /* eslint-enable guard-for-in */
};

// Implementation
// ----------------

/**
 * Store a clone of the data at the given key.
 *
 * @param  {string}  key    The key
 * @param  {mixed}   value  The value
 * @private
 */
CacheStore.prototype._setData = function(key, value) {
    this.data[key] = JSON.parse(JSON.stringify(value));
};

/**
 * Get a clone of the data at the given key.
 *
 * @param    {string}  key  The key
 * @returns  {mixed}   The data
 * @private
 */
CacheStore.prototype._getData = function(key) {
    return this.data[key] ? JSON.parse(JSON.stringify(this.data[key])) : null;
};

/**
 * Clears the data at the given key.
 *
 * @param  {string}  key  The key
 * @private
 */
CacheStore.prototype._clearData = function(key) {
    if (this.data[key]) {
        delete this.data[key];
    }
};

/**
 * Set the timeout for the given key.
 *
 * @param  {string}  key       The key
 * @param  {number}  lifetime  The lifetime of the value in seconds
 * @private
 */
CacheStore.prototype._setExpiration = function(key, lifetime) {
    var that = this;

    this._clearExpiration();

    if (lifetime > 0) {
        this.timers[key] = setTimeout(function() {
            that.unset(key);
        }, lifetime * 1000);
    }
};

/**
 * Clears the timeout for the given key.
 *
 * @param  {string}  key  The key
 * @private
 */
CacheStore.prototype._clearExpiration = function(key) {
    if (this.timers[key]) {
        clearTimeout(this.timers[key]);
        delete this.timers[key];
    }
};
